import { Position, type Edge, type Node } from '@xyflow/react'
import { layoutFromMap } from 'entitree-flex'

import type { DepartmentFlatNode, DepartmentFlatTree } from '../../Contracts/DepartmentTree.types'

const nodeWidth = 300
const nodeHeight = 200

const Orientation = {
    Vertical: 'vertical',
    Horizontal: 'horizontal',
} as const

const entitreeSettings = {
    clone: true,
    enableFlex: true,
    firstDegreeSpacing: 100,
    nextAfterAccessor: 'spouses',
    nextAfterSpacing: 100,
    nextBeforeAccessor: 'siblings',
    nextBeforeSpacing: 100,
    nodeHeight,
    nodeWidth,
    orientation: Orientation.Vertical,
    rootX: 0,
    rootY: 0,
    secondDegreeSpacing: 100,
    sourcesAccessor: 'parents',
    sourceTargetSpacing: 100,
    targetsAccessor: 'children',
}

const { Top, Bottom, Left, Right } = Position

export const layoutElements = (
    tree: DepartmentFlatTree,
    rootId: string | number,
    direction: 'TB' | 'LR' = 'TB'
): { nodes: Node[]; edges: Edge[] } => {
    const isTreeHorizontal = direction === 'LR'

    const { nodes: entitreeNodes, rels: entitreeEdges } = layoutFromMap<DepartmentFlatNode>(rootId, tree, {
        ...entitreeSettings,
        orientation: isTreeHorizontal ? Orientation.Horizontal : Orientation.Vertical,
    })

    const nodes: Node[] = []
    const edges: Edge[] = []

    entitreeEdges.forEach((edge) => {
        const sourceNode = edge.source.id
        const targetNode = edge.target.id

        const isTargetSpouse = !!edge.target.isSpouse
        const isTargetSibling = !!edge.target.isSibling

        let sourceHandle: Position
        let targetHandle: Position

        if (isTargetSpouse) {
            sourceHandle = isTreeHorizontal ? Bottom : Right
            targetHandle = isTreeHorizontal ? Top : Left
        } else if (isTargetSibling) {
            sourceHandle = isTreeHorizontal ? Top : Left
            targetHandle = isTreeHorizontal ? Bottom : Right
        } else {
            sourceHandle = isTreeHorizontal ? Right : Bottom
            targetHandle = isTreeHorizontal ? Left : Top
        }

        edges.push({
            id: 'e' + sourceNode + targetNode,
            source: sourceNode,
            target: targetNode,
            type: 'smoothstep',
            animated: false,
            sourceHandle,
            targetHandle,
        })
    })

    entitreeNodes.forEach((node) => {
        const isSpouse = !!node?.isSpouse
        const isSibling = !!node?.isSibling
        const isRoot = node?.id === rootId

        let sourcePosition: Position
        let targetPosition: Position

        if (isSpouse) {
            sourcePosition = isTreeHorizontal ? Bottom : Right
            targetPosition = isTreeHorizontal ? Top : Left
        } else if (isSibling) {
            sourcePosition = isTreeHorizontal ? Top : Left
            targetPosition = isTreeHorizontal ? Bottom : Right
        } else {
            sourcePosition = isTreeHorizontal ? Right : Bottom
            targetPosition = isTreeHorizontal ? Left : Top
        }

        nodes.push({
            id: node.id,
            type: 'custom',
            sourcePosition,
            targetPosition,
            data: { label: node.name, direction, isRoot, ...node },
            width: nodeWidth,
            height: nodeHeight,
            position: {
                x: node.x,
                y: node.y,
            },
        })
    })

    return { nodes, edges }
}
